import { apm } from '@elastic/apm-rum'
import cn from 'classnames'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import Loader from '../../../../Loader'
import { useAppState, useScrollToTop } from '../../../../../utils/hooks'
import Utils from '../../../../../utils/utils'
import { appError } from '../../../../../redux/slices/app'
import { AppDepositAttribution, Currency, PaymentFlowSessionAction } from '../../../../../utils/types'
import { useThemes } from '../../../../../utils/themes'
import { CHECKOUT_APPLE_PAY_PROCESSOR, CHECKOUT_WALLET_PROCESSORS } from '../utils/checkoutConfigs'
import { AccountLikeObject, ProcessorLikeObject, useInitiatePayment } from '../utils/useInitiatePayment'
import useDepositResultPage from '../utils/useDepositResultPage'
import { useDashboardAnalytics } from '../../../Onboarding/useDashboardAnalytics'
import { mapPaymentMethod, normalizeProcessorName } from '../../../../../utils/floodlightEvents'

import styles from './style.module.scss'

// THPRT-6277 / TMJV-902: Checkout.com Frames -> Flow migration (scaffold).
// Frames is decommissioned by Checkout.com on 30-Jun-2026. This component
// replaces the client-side Frames card fields + Frames.submitCard() tokenisation
// with the hosted Flow drop-in: the BE creates a Payment Session, we mount Flow
// with it, and Checkout.com hosts the card/wallet UI, 3DS, and tokenisation.
//
// Routed from the deposit method switch for the BE Flow sub-processors
// (Checkout CC / Apple Pay / Google Pay, TMJV-902). The legacy Frames records
// are kept live BE-side behind a per-record switch but are no longer surfaced
// by the FE.
//
// The SDK is loaded from its CDN (it attaches window.CheckoutWebComponents) to
// stay on TypeScript 4.2.4 — see the note in checkoutFlow.d.ts.

const FLOW_CONTAINER_ID = 'checkout-flow-container'
const CHECKOUT_FLOW_SCRIPT = 'https://checkout-web-components.checkout.com/index.js'

// Error codes the Flow drop-in already surfaces inline, so we must NOT also raise
// a global error dialog for them — the inline message is the user-facing feedback
// (a double-report otherwise stacks our "Error" modal over Flow's own text):
//   - component_invalid:          field validation, e.g. "This field is required"
//   - payment_request_declined:   issuer/acquirer decline, e.g. "Not enough funds"
// Any code NOT listed here still falls through to the modal as a safety net, so an
// unrecognised error stays visible rather than being silently swallowed.
const FLOW_INLINE_ERROR_CODES = ['component_invalid', 'payment_request_declined']

// Safety-net wait for the SDK's onReady callback. If it never fires after a
// successful mount, clear the loader so the drop-in is not covered forever.
const FLOW_READY_TIMEOUT_MS = 15_000

// Map the portal theme tokens onto Checkout.com Flow appearance tokens so the
// deposit step looks consistent with the rest of the portal.
const useFlowAppearance = (): CheckoutFlowAppearance => {
    const { theme } = useThemes()
    // ThinkMarkets brand green ($accent-green_) — the Pay button + accents, so
    // the drop-in matches the portal instead of Checkout's default blue.
    const colorAction = '#138518'
    if (theme === 'dark') {
        return {
            colorPrimary: '#f9f9f9',
            colorAction,
            colorBackground: '#181f25',
            colorFormBackground: '#181f25',
            colorBorder: '#878f93',
            colorFormBorder: '#878f93',
            colorError: '#d1425a',
        }
    }
    return {
        colorPrimary: '#0e1d31',
        colorAction,
        colorBackground: '#f9f9f9',
        colorFormBackground: '#f9f9f9',
        colorBorder: '#9fa5ad',
        colorFormBorder: '#9fa5ad',
        colorError: '#df1a3c',
    }
}

// THPRT-6277: a Checkout.com session is bound to a fixed amount/currency. Keying
// it in one place keeps the bind site (mount) and the compare site (teardown)
// from drifting — if they did, a stale session would silently never tear down.
const sessionKey = (amount: string, currency: Currency | null) => `${amount}|${currency?.id ?? ''}`

interface CheckoutFlowProps {
    processor: ProcessorLikeObject
    account: AccountLikeObject
    amount: string
    currency: Currency | null
    isAmountFormValid: boolean
    // THPRT-6558: app attribution from the /checkoutcom URL params (in-app web view).
    appDeposit?: AppDepositAttribution
}

const CheckoutFlow: FC<CheckoutFlowProps> = ({ processor, account, amount, currency, isAmountFormValid, appDeposit }) => {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const appearance = useFlowAppearance()
    const getResultPage = useDepositResultPage()

    const [scriptLoaded, setScriptLoaded] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isMounted, setIsMounted] = useState<boolean>(false)
    // THPRT-6277 (Apple Pay QR): set when Checkout's applepay.isAvailable() reports the
    // Apple Pay button cannot render on this browser/device, so we show an inline
    // fallback instead of an empty container.
    const [walletUnavailable, setWalletUnavailable] = useState<boolean>(false)
    const flowComponentRef = useRef<CheckoutFlowComponent | null>(null)
    // Our tfx transaction id, captured from initiatePayment. The result page
    // resolves the deposit by this, not the Checkout.com pay_… id.
    const transactionIdRef = useRef<string | null>(null)
    const { liveAccounts } = useAppState()
    const { fireDeposit } = useDashboardAnalytics()
    const matchedAccount = liveAccounts.find(a => a.account.id === account.account.id)
    // The amount|currency the mounted session was created for. If the user edits
    // the amount after mounting, the session is stale and we must re-create it.
    const mountedKeyRef = useRef<string | null>(null)
    // Safety net for onReady: if the SDK mounts but never fires onReady, the
    // loader would cover a renderable drop-in forever, so we clear it on a timeout.
    const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // mountFlow is fire-and-forget (the onFlowSession callback is synchronous), so
    // its post-await state/timer writes can land after the component unmounts — e.g.
    // switching payment method remounts via key={processor.id}. Guard those writes
    // so they no-op (and don't reassign a timer the cleanup already cleared) once
    // we're unmounted (THPRT-6277 review M-1).
    const isMountedRef = useRef(true)
    // Re-entrancy guard for the Continue -> initiatePayment -> mountFlow attempt. The
    // loader safety-timeout (armed before the awaits) can clear isLoading and re-enable
    // the Continue button while a mount is still in flight; without this guard a second
    // click would start a concurrent BE session + mountFlow sharing the same refs and
    // orphan a drop-in (THPRT-6323 review). Kept in a ref (not state) so it gates the
    // handler synchronously without an extra render.
    const isMountingRef = useRef(false)

    // Wallet tiles (Apple/Google Pay) mount a single hosted pay button with no
    // surrounding copy. Show a short prompt once it is up so the client knows to
    // use it to complete the deposit (Zeeshan UX review, THPRT-6277).
    const isWallet = CHECKOUT_WALLET_PROCESSORS.includes(processor.name)
    const isApplePay = processor.name === CHECKOUT_APPLE_PAY_PROCESSOR

    useScrollToTop()

    // Tear the mounted drop-in down and return to the Continue step so the next
    // attempt creates a fresh Checkout payment session. Used when the amount changes
    // (stale session) and to recover from a terminal wallet error such as an expired
    // Apple Pay session — the QR modal times out after ~5 min and the component
    // removes its button, which would otherwise leave the user with no way to restart
    // (THPRT-6323).
    const resetFlow = useCallback(() => {
        if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current)
        flowComponentRef.current?.unmount()
        flowComponentRef.current = null
        mountedKeyRef.current = null
        setIsMounted(false)
    }, [])

    useEffect(() => {
        // The SDK attaches window.CheckoutWebComponents. If it is already present
        // (component re-mounted / re-rendered), reuse it instead of re-injecting
        // the ~1.3MB script. The id lets loadScript de-dupe the tag as a backstop.
        if (window.CheckoutWebComponents) {
            setScriptLoaded(true)
            return
        }
        // A CDN / network / CSP failure must surface an actionable error instead of
        // leaving the deposit step on a permanent loader with Continue disabled — the
        // SDK's onload would otherwise never fire and scriptLoaded would stay false
        // (THPRT-6277 review M-1).
        Utils.loadScript(
            CHECKOUT_FLOW_SCRIPT,
            () => { if (isMountedRef.current) setScriptLoaded(true) },
            'checkout-flow-sdk',
            false,
            () => { if (isMountedRef.current) dispatch(appError(Utils.customError('Checkout.com Flow failed to load.'))) },
        )
    }, [dispatch])

    // Tear down the Flow drop-in on unmount so a re-entry / re-render does not
    // leave an orphaned iframe (the Flow equivalent of the TMJV-940 Frames race).
    useEffect(() => () => {
        isMountedRef.current = false
        if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current)
        flowComponentRef.current?.unmount()
        flowComponentRef.current = null
    }, [])

    // The Checkout.com session is created for a fixed amount/currency at Continue.
    // The amount field stays editable, so if the user changes it after the drop-in
    // mounts, the live session is stale and the drop-in's Pay button would charge
    // the previous amount. Tear the drop-in down and return to the Continue step so
    // a fresh session is created for the new amount.
    useEffect(() => {
        if (!isMounted || mountedKeyRef.current === null) return
        if (mountedKeyRef.current !== sessionKey(amount, currency)) resetFlow()
    }, [amount, currency, isMounted, resetFlow])

    const mountFlow = async (action: PaymentFlowSessionAction) => {
        if (!isMountedRef.current) { isMountingRef.current = false; return }
        if (!window.CheckoutWebComponents) {
            isMountingRef.current = false
            setIsLoading(false)
            dispatch(appError(Utils.customError('Checkout.com Flow failed to load.')))
            return
        }
        // Runtime-guard the BE-supplied environment: `as` is compile-only, so a
        // misconfigured record value (e.g. "staging") would otherwise pass straight
        // to the SDK. Accept only the two valid literals; fall back otherwise.
        const environment: 'sandbox' | 'production' =
            action.environment === 'production' || action.environment === 'sandbox'
                ? action.environment
                : (Utils.isTestEnv() ? 'sandbox' : 'production')
        // Arm the loader safety-net BEFORE the awaits below. The instance init and the
        // Apple Pay isAvailable() check can fail to settle — e.g. re-initialising Apple
        // Pay after an expired session leaves the loader spinning forever (THPRT-6323) —
        // so the timeout must already be scheduled when we enter them. onReady clears it
        // on the happy path; the unavailable branch and catch clear it otherwise.
        if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current)
        readyTimeoutRef.current = setTimeout(() => { if (isMountedRef.current) setIsLoading(false) }, FLOW_READY_TIMEOUT_MS)
        try {
            // The loader is async — await it before calling create().
            const checkout = await window.CheckoutWebComponents({
                // publicKey + environment come from the resolved BE SubProcessor
                // record (record-driven), so they always match the created session.
                publicKey: action.publicKey,
                environment,
                locale: i18n.language,
                paymentSession: action.paymentSession,
                appearance,
                onReady: () => {
                    if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current)
                    setIsLoading(false)
                },
                onPaymentCompleted: (_component, _payment) => {
                    // The result page reads BE/webhook-confirmed state; this
                    // callback only routes there (it can race ahead of the webhook).
                    // THPRT-6317: route by our tfx transaction id only. Treat an empty
                    // string as absent (|| not ??) and do NOT fall back to Checkout's
                    // pay_… id — getPaymentInfo resolves by the tfx id, so a pay_… id
                    // can never resolve and only trades the error for a failed poll.
                    history.push(getResultPage(transactionIdRef.current || undefined))
                },
                onError: (_component, error) => {
                    setIsLoading(false)
                    if (error?.code === 'payment_request_declined' && matchedAccount) {
                        fireDeposit('failed', {
                            amount,
                            currency: currency?.code,
                            paymentMethod: mapPaymentMethod(processor.type),
                            processor: normalizeProcessorName(processor.displayName ?? processor.name),
                            depositStatus: 'DECLINED',
                            transactionId: transactionIdRef.current || undefined,
                            accountId: matchedAccount.account.accountNumber,
                            accountType: matchedAccount.account.accountTradingType?.accountTradingType,
                            productTier: matchedAccount.platformAccountType,
                            platform: matchedAccount.account.platform?.name,
                        })
                    }
                    // Field-level validation is already shown inline by the drop-in;
                    // don't double-report it as a global error dialog.
                    if (error?.code && FLOW_INLINE_ERROR_CODES.includes(error.code)) return
                    // The SDK message is server-generated and may carry a payment
                    // session id (pay_…) that Utils.customError's digit-only scrub
                    // would not catch, so send a generic, code-tagged label to APM
                    // and show the raw message only in the user modal (review M-5).
                    apm.captureError(`Checkout.com Flow error [code=${error?.code ?? 'unknown'}]`)
                    dispatch(appError({ type: 'custom', text: error?.message ?? 'An unexpected error occurred' }))
                    // THPRT-6323: the Apple Pay drop-in cannot be reused after a terminal
                    // error such as an expired Apple Pay session (its QR modal times out
                    // after ~5 min and the button is removed). Tear it down so the Continue
                    // button returns and the next attempt creates a fresh session. Gate on
                    // isApplePay, NOT isWallet: Google Pay uses the generic 'flow' drop-in
                    // (like Card) and should retry in place rather than be torn down.
                    if (isApplePay) resetFlow()
                },
            })
            // The await above can outlast the component (e.g. a fast payment-method
            // switch remounts via key={processor.id}); bail before mounting into a
            // detached node or writing state/assigning a timer (THPRT-6277 review M-1).
            if (!isMountedRef.current) return
            // THPRT-6277 (Apple Pay QR): for the Apple Pay tile create the dedicated
            // 'applepay' component and let Checkout decide capability via isAvailable().
            // It returns true on cross-device-capable browsers (iOS 18 QR flow), which is
            // why the tile is no longer pre-hidden on window.ApplePaySession. If Checkout
            // reports it unavailable (e.g. the deposit domain is not registered with the
            // Apple Pay merchant, or no reachable Apple device), show an inline fallback
            // instead of mounting an empty button. Card + Google Pay keep the generic
            // 'flow' drop-in unchanged.
            const component = isApplePay ? checkout.create('applepay') : checkout.create('flow')
            if (isApplePay) {
                const available = await component.isAvailable()
                // isAvailable() is async; re-check the mount guard once before any
                // state/DOM write (the 'flow' path has no await here, so needs none).
                if (!isMountedRef.current) return
                if (!available) {
                    if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current)
                    // The session bound at onFlowSession is now unusable; clear its key so
                    // no stale binding lingers for a future amount-change / mount path.
                    mountedKeyRef.current = null
                    setIsLoading(false)
                    setWalletUnavailable(true)
                    return
                }
            }
            component.mount(`#${FLOW_CONTAINER_ID}`)
            flowComponentRef.current = component
            setIsMounted(true)
            // The safety-net timeout armed before the awaits stays running until the
            // drop-in's onReady clears it (or it fires and clears the loader itself).
        } catch (e) {
            // Mirror onError: keep any server-generated detail out of APM, show it
            // only to the user (THPRT-6277 review M-5). Telemetry fires regardless,
            // but skip the state write/dispatch if we unmounted mid-await.
            apm.captureError('Checkout.com Flow mount error')
            if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current)
            if (!isMountedRef.current) return
            setIsLoading(false)
            dispatch(appError({ type: 'custom', text: (e as Error)?.message ?? 'An unexpected error occurred' }))
        } finally {
            // The attempt has concluded (mounted, unavailable, or errored); release the
            // re-entrancy guard so a legitimate later retry (e.g. after resetFlow on an
            // expired Apple Pay session) can run. Covers every in-try exit incl. the
            // isMountedRef early-returns and the unavailable branch (THPRT-6323 review).
            isMountingRef.current = false
        }
    }

    const { initiatePayment, captchaModal } = useInitiatePayment(processor, account, () => handleContinue())

    const handleContinue = async () => {
        // Ignore re-entrant clicks while an attempt is still in flight (see isMountingRef).
        if (!currency || isMountingRef.current) return
        isMountingRef.current = true
        setIsLoading(true)
        // Clear any prior unavailable state so a retry re-evaluates isAvailable().
        setWalletUnavailable(false)
        let sessionReceived = false
        await initiatePayment({
            amount,
            currencyId: currency.id,
            // THPRT-6558: forward the in-app deposit attribution (AFS-2.1).
            appDeposit,
            // No client-side token and no `type` discriminator: the BE routes to the
            // Flow path by the sub-processor's own processorId (Checkout CC / Apple
            // Pay / Google Pay) and creates the Checkout.com Payment Session, returned
            // via a `flowSession` action (TMJV-902 contract).
            onFlowSession: (action, transactionId) => {
                sessionReceived = true
                transactionIdRef.current = transactionId
                // Bind the session to the amount/currency it was created for, so a
                // later edit of the amount field tears the stale drop-in down.
                mountedKeyRef.current = sessionKey(amount, currency)
                mountFlow(action)
            },
        })
        // If the BE returned an error or a non-flowSession action, mountFlow never
        // ran — clear the loader and the mounting guard so the user is not stuck. On the
        // success path mountFlow owns clearing the guard (in its finally) and the loader
        // is cleared by Flow's onReady once the drop-in has rendered.
        if (!sessionReceived) {
            isMountingRef.current = false
            setIsLoading(false)
        }
    }

    return (
        <div>
            {captchaModal}
            {(isLoading || !scriptLoaded) && <Loader />}

            {isWallet && isMounted && <p className={styles.walletPrompt}>{t('common:checkoutWalletPrompt')}</p>}

            {walletUnavailable && <p className={styles.walletPrompt}>{t('common:applePayUnavailable')}</p>}

            <div
                id={FLOW_CONTAINER_ID}
                className={cn(styles['credit-card-section'], {
                    [styles.wallet]: isWallet,
                })}
            />

            {!isMounted && !walletUnavailable && (
                <button
                    type="button"
                    onClick={handleContinue}
                    disabled={!isAmountFormValid || isLoading || !scriptLoaded}
                    className={cn('button', 'green', styles.button)}
                >
                    {t('common:continue')}
                </button>
            )}
        </div>
    )
}

export default CheckoutFlow
